import { Box, Group, SegmentedControl, Text } from '@mantine/core';
import { mixHex } from '../../helpers/brandColors';

// A likeness of the open chat (header, messages, product cards, input, launcher) painted with the
// resolved colors of one side, so every change shows before it is saved. Plain boxes, not the
// widget bundle. The role being hovered or edited (`activeRole`) is outlined.

const SAMPLE_PRODUCTS = [
  { name: 'Linen tote', price: '34.00' },
  { name: 'Ceramic mug', price: '18.90' },
];

export const SIDE_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const WidgetColorPreview = ({ resolved, side, onSideChange, mode, activeRole, title }) => {
  const p = resolved[side].palette;
  const border = mixHex(p.text, p.background, 0.12);
  const ring = (role) =>
    activeRole === role ? { outline: '2px solid var(--mantine-color-blue-5)', outlineOffset: 2 } : undefined;
  const note =
    mode === 'light' && side === 'dark'
      ? "Shoppers won't see this unless Appearance is Dark or Match visitor's device."
      : mode === 'auto'
        ? 'Shoppers see the side that matches their device.'
        : null;
  // A round main-color button: the header avatar and the send button.
  const dot = (size, children) => (
    <Box
      data-role="primary"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: p.primary,
        color: p.onPrimary,
        display: 'grid',
        placeItems: 'center',
        fontSize: 12,
        ...ring('primary'),
      }}
    >
      {children}
    </Box>
  );
  const bubble = (role, style, children) => (
    <Box
      data-role={role}
      style={{ padding: '8px 10px', borderRadius: 12, fontSize: 13, maxWidth: '80%', ...style, ...ring(role) }}
    >
      {children}
    </Box>
  );

  return (
    <Box>
      <Group justify="space-between" mb="xs">
        <Text size="xs" tt="uppercase" fw={600} c="dimmed">
          Preview
        </Text>
        <SegmentedControl
          size="xs"
          value={side}
          onChange={onSideChange}
          aria-label="Preview side"
          data={SIDE_OPTIONS}
        />
      </Group>
      <Box
        data-testid="widget-preview"
        style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${border}`, background: p.background }}
      >
        <Group
          gap={8}
          data-role="background"
          style={{
            padding: 10,
            background: p.background,
            color: p.text,
            borderBottom: `1px solid ${border}`,
            ...ring('background'),
          }}
        >
          {dot(28, 'S')}
          <Text size="sm" fw={600} style={{ color: p.text }}>
            {title || 'Sales Assistant'}
          </Text>
          <Text size="sm" ml="auto" style={{ color: p.textMuted }}>
            ✕
          </Text>
        </Group>
        <Box
          data-role="surface"
          style={{ background: p.surface, padding: 10, display: 'grid', gap: 8, ...ring('surface') }}
        >
          {bubble(
            'background',
            { background: p.background, color: p.text, border: `1px solid ${border}` },
            'Hi! Looking for something in particular?'
          )}
          {bubble('primary', { background: p.primary, color: p.onPrimary, justifySelf: 'end' }, 'A gift under 50')}
          <Group gap={8} wrap="nowrap">
            {SAMPLE_PRODUCTS.map((product) => (
              <Box
                key={product.name}
                data-role="background"
                style={{
                  flex: 1,
                  background: p.background,
                  border: `1px solid ${border}`,
                  borderRadius: 10,
                  padding: 8,
                  ...ring('background'),
                }}
              >
                <Box style={{ height: 40, borderRadius: 6, background: mixHex(p.text, p.background, 0.15) }} />
                <Text size="xs" fw={600} mt={6} data-role="text" style={{ color: p.text, ...ring('text') }}>
                  {product.name}
                </Text>
                <Text size="xs" data-role="textMuted" style={{ color: p.textMuted, ...ring('textMuted') }}>
                  {product.price}
                </Text>
                <Text size="xs" fw={600} data-role="primary" style={{ color: p.primary, ...ring('primary') }}>
                  View →
                </Text>
              </Box>
            ))}
          </Group>
          <Text size="xs" data-role="textMuted" style={{ color: p.textMuted, ...ring('textMuted') }}>
            Just now
          </Text>
        </Box>
        <Group
          gap={8}
          wrap="nowrap"
          data-role="background"
          style={{ padding: 10, background: p.background, borderTop: `1px solid ${border}`, ...ring('background') }}
        >
          <Box
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 16,
              background: p.surface,
              color: p.textMuted,
              fontSize: 13,
            }}
          >
            Ask me anything…
          </Box>
          {dot(30, '➤')}
        </Group>
      </Box>
      {note && (
        <Text size="xs" c="dimmed" mt={6}>
          {note}
        </Text>
      )}
    </Box>
  );
};

export default WidgetColorPreview;
